import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';

/**
 * The Plus promo calendar's front end. Admins put dated cards on the calendar
 * (/admin, Pop-ups); this shows the first one that is live, fits the viewer, and
 * has not been snoozed. Each card snoozes on its own, so dismissing one does not
 * hide the next. When a card's end date passes the server stops returning it and
 * nothing here needs a change. Replaces the hand-coded CharterSaleModal /
 * OfferModal pattern for everything after the October offer.
 */
export type PromoEntry = {
  id: string;
  title: string;
  kicker?: string;
  body?: string;
  imageUrl?: string;
  ctaLabel?: string;
  ctaUrl: string;
  audience: 'everyone' | 'signed_out' | 'non_members' | 'members';
  pages: 'home' | 'all';
  snoozeDays: number;
};

const APPEAR_AFTER_MS = 3200;
const QUIET_PATHS = ['/offer', '/limelite', '/charter', '/auth', '/login', '/reset-password', '/trial', '/billing', '/admin', '/checkout', '/plans'];
const QUIET_PREFIXES = ['/my-store/', '/interview/', '/show/', '/show-host/', '/session/', '/subscribe/', '/video-enhancer', '/scene-edit'];

const snoozeKey = (id: string) => `hj_promo_${id}`;
const snoozed = (e: PromoEntry) => {
  if (!e.snoozeDays) return false;
  try {
    const t = parseInt(localStorage.getItem(snoozeKey(e.id)) || '0', 10);
    return !!t && Date.now() - t < e.snoozeDays * 86400000;
  } catch { return false; }
};
const snooze = (e: PromoEntry) => { try { localStorage.setItem(snoozeKey(e.id), String(Date.now())); } catch { /* shows again; survivable */ } };

const PromoPopup: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { canUseTools, loading: subLoading } = useSubscription();
  const [entries, setEntries] = useState<PromoEntry[] | null>(null);
  const [current, setCurrent] = useState<PromoEntry | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch('/api/promos/active').then(r => (r.ok ? r.json() : { entries: [] })).then(d => { if (alive) setEntries(Array.isArray(d.entries) ? d.entries : []); }).catch(() => { if (alive) setEntries([]); });
    return () => { alive = false; };
  }, []);

  const quiet = QUIET_PATHS.includes(location.pathname) || QUIET_PREFIXES.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    if (!entries || !entries.length || quiet || open) return;
    if (isAuthenticated && subLoading) return;
    // A signed-out browser can still carry a cached tier, so membership only
    // counts when the person is actually signed in.
    const member = isAuthenticated && !!canUseTools;
    const fits = (e: PromoEntry) => {
      if (e.pages === 'home' && location.pathname !== '/') return false;
      if (e.audience === 'signed_out') return !isAuthenticated;
      if (e.audience === 'members') return member;
      if (e.audience === 'non_members') return !member;
      return true;
    };
    const next = entries.find(e => fits(e) && !snoozed(e));
    if (!next) return;
    const id = setTimeout(() => {
      // Another dialog already up (the October offer, a checkout step): wait for the next page.
      if (document.querySelector('[role="dialog"]')) return;
      setCurrent(next); setOpen(true);
    }, APPEAR_AFTER_MS);
    return () => clearTimeout(id);
  }, [entries, quiet, isAuthenticated, subLoading, canUseTools, location.pathname, open]);

  if (!current) return null;
  const close = () => { snooze(current); setOpen(false); };
  // Every offer carries the way in for someone not ready for it: the 3-day trial
  // (Joe, Oct 15 2026). Non-members only, and not when the card IS the trial.
  const member = isAuthenticated && !!canUseTools;
  const showTrial = !member && !/code=bookit3/i.test(current.ctaUrl);
  const trial = () => { snooze(current); setOpen(false); navigate('/?code=bookit3&utm_source=plus&utm_medium=popup&utm_campaign=promo_trial_fallback'); };
  const go = () => {
    snooze(current); setOpen(false);
    if (/^https?:\/\//i.test(current.ctaUrl)) window.location.href = current.ctaUrl; else navigate(current.ctaUrl);
  };

  // Same paper-and-ink card as the October offer pop-up, so the calendar's cards
  // all look like one family.
  const C = { paper: '#FFFBF0', ink: '#0A0A0A', ink2: '#3A3732', yellow: '#FFD60A' };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="sm:max-w-[480px] p-0" style={{ background: C.paper, color: C.ink, borderRadius: 22, border: `2px solid ${C.ink}`, fontFamily: "'Geist', system-ui, sans-serif", maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
        {current.imageUrl && (
          <img src={current.imageUrl} alt="" style={{ display: 'block', width: '100%', aspectRatio: '16 / 9', objectFit: 'cover', borderRadius: '20px 20px 0 0', borderBottom: `2px solid ${C.ink}` }} />
        )}
        <div style={{ padding: '24px 24px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {current.kicker && (
            <div style={{ display: 'inline-flex', alignSelf: 'flex-start', padding: '7px 12px', background: C.ink, color: C.yellow, borderRadius: 999, fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: 11.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{current.kicker}</div>
          )}
          <h2 style={{ margin: 0, fontFamily: "'Red Hat Display', sans-serif", fontWeight: 900, fontSize: 32, lineHeight: 1.02, letterSpacing: '-0.03em', color: C.ink }}>{current.title}</h2>
          {current.body && <p style={{ margin: 0, fontSize: 16, lineHeight: 1.45, color: C.ink2, whiteSpace: 'pre-line' }}>{current.body}</p>}
          <button onClick={go} style={{ width: '100%', minHeight: 54, marginTop: 6, borderRadius: 999, border: 'none', cursor: 'pointer', background: C.ink, color: C.paper, fontSize: 17, fontWeight: 700, fontFamily: 'inherit' }}>
            {current.ctaLabel || 'Save my seat'}
          </button>
          {showTrial && (
            <p style={{ margin: '2px 0 0', textAlign: 'center', fontSize: 14.5, lineHeight: 1.4, color: C.ink2 }}>
              Not ready for that? <button onClick={trial} style={{ border: 'none', background: 'none', padding: 0, cursor: 'pointer', fontSize: 14.5, fontWeight: 700, color: C.ink, textDecoration: 'underline', fontFamily: 'inherit' }}>Try Plus free for 3 days</button>
            </p>
          )}
          <button onClick={close} style={{ alignSelf: 'center', minHeight: 40, padding: '0 10px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: C.ink, textDecoration: 'underline', fontFamily: 'inherit' }}>Maybe later</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PromoPopup;
